import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BlockType, BlockWithNotes } from '../../../../../config/api/models/Block';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { usePagesByType } from '../../../hooks';
import { useUser } from '../../../../shared/hooks';
import { BlockResize } from '../../../../shared/components';
import { TrajectorySectionOrWorksheetType } from '../../../../../config/api/models/Trajectory';
import {
  BlockHeader,
  EditBlockContentButton,
  RelativeWrapper,
  WhiteContentWrapper,
} from './atoms/Atoms';
import { BlockIcon } from '../../BlockIcon';
import { FeedbackModule } from '../../../../feedback';
import { AddContentDialog } from '../AddBlockButton/AddContentDialog';
import { useAppDispatch, useAppSelector } from '../../../../shared/hooks/useAppDispatch';
import { patchBlock } from '../../../../shared/slices/blocks/api/blocks';
import { ClientSettings } from 'src/config/api/models/Exercise';
import { SpriteIconsLight } from 'src/config/api/models/FontAwesome';
import { useGetStudentBookmarkQuery } from 'src/modules/shared/slices/bookmark/api/bookmarkApi';

const DEFAULT_IFRAME_HEIGHT = 750;

type Props = {
  block: BlockWithNotes;
  pageId: string;
  bookId: string;
  mode: 'read' | 'edit';
  type: TrajectorySectionOrWorksheetType;
  feedbackEnabled: boolean;
  ref?: Element | undefined;
  selectedBlock?: string | undefined;
  onSelectBlock: Function;
  readOnly?: boolean | undefined;
};

const IframeBlock: FC<Props> = ({
  block,
  pageId,
  bookId,
  mode,
  type,
  selectedBlock,
  onSelectBlock,
  readOnly,
}) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { isStudent } = useUser();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const bookmarkMode = useAppSelector((state) => state.bookmark.bookmarkMode);
  // rtk
  const { data: studentBookmarksData } = useGetStudentBookmarkQuery(bookId as string, {
    skip: !isStudent,
  });
  const bookmark = useMemo(() => {
    if (studentBookmarksData === undefined) return undefined;
    return studentBookmarksData;
  }, [studentBookmarksData]);

  const isBookmarked =
    bookmark?.length && bookmark.filter((item) => item.blockId === block.id).length;
  const [blockHeight] = useState(
    block.client_settings?.height !== undefined && !isNaN(block.client_settings?.height)
      ? Number(block.client_settings!!.height)
      : DEFAULT_IFRAME_HEIGHT
  );
  const pages = usePagesByType(type);

  const [showFeedback] = useState(false);
  const blockHeightRef = useRef(blockHeight);

  useEffect(() => {
    if (!!block.client_settings) {
      // if blocks update, the blockHeight has to be reInitialized. blocks might change after initializing useState
      blockHeightRef.current =
        block.client_settings?.height !== undefined && !isNaN(block.client_settings?.height)
          ? Number(block.client_settings!!.height)
          : DEFAULT_IFRAME_HEIGHT;
    }
  }, [block, block.client_settings]);

  const handleResize = useCallback(
    (value: number) => {
      blockHeightRef.current = value;

      if (pages.data && pages.data.length > 0) {
        const client_settings: ClientSettings = {
          height: value,
          width: !!block.client_settings ? block.client_settings.width : 1090,
        };

        dispatch(
          patchBlock({
            bookId,
            pageId,
            blockId: block.id,
            payload: {
              ...block,
              client_settings,
            },
          })
        );
      }
    },
    [pages, block, bookId, dispatch, pageId]
  );

  const Iframe = useCallback(() => {
    return (
      <iframe
        title={`iframe_${block.id}`}
        src={(block.content as string) || 'about:blank'}
        id={`iframe_${block.id}`}
        name={`iframe_${block.id}`}
        width="100%"
        height={blockHeightRef.current}
        scrolling="auto"
        frameBorder="0"
        allow="fullscreen"
        loading="lazy"
      />
    );
  }, [block.content, block.id]);

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);

  const handleDialogOpen = (isOpen: boolean) => {
    setDialogOpen((prev) => !prev);
    onSelectBlock(isOpen ? block.id : undefined);
  };
  if (block.id === '6022908b096a61b41b7027e6') {
    console.log(blockHeightRef.current);
  }
  return (
    <>
      <AddContentDialog
        dialogOpen={selectedBlock === block.id && dialogOpen}
        setDialogOpen={handleDialogOpen}
        pageId={pageId}
        bookId={bookId as string}
        edit={selectedBlock === block.id}
        blockId={selectedBlock}
        content={block.content as string}
        type="iframe"
        validationType="isURL"
      />
      <RelativeWrapper>
        <WhiteContentWrapper
          isMobile={isMobile}
          bookmarkMode={bookmarkMode}
          isBookmarked={isBookmarked}
          height={blockHeightRef.current}
        >
          <BlockHeader>
            <BlockIcon type={BlockType.IFRAME} />
            {!readOnly && (
              <EditBlockContentButton
                faIcon={SpriteIconsLight.PENCIL}
                onClick={() => handleDialogOpen(true)}
              />
            )}
          </BlockHeader>

          {showFeedback && (
            <FeedbackModule pageId={pageId} blockId={block.id} context="block" mode="embedded" />
          )}
          {
            {
              read: <Iframe />,
              edit: (
                <BlockResize height={blockHeightRef.current} onHeightChange={handleResize}>
                  <Iframe />
                </BlockResize>
              ),
            }[mode]
          }
        </WhiteContentWrapper>
      </RelativeWrapper>
    </>
  );
};

export default IframeBlock;
